import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { STORAGE_KEYS } from '../constants/storageKeys';
import type { User, UserRole } from '../types/User';
import { ApiError, buildApiError } from '../utils/apiErrors';
import {
  clearStoredSession,
  endSessionIfExpired,
  getActiveProjectId,
  reloadApp,
  SESSION_ENDED_EVENT,
  setActiveProjectId,
} from '../utils/session';
import { isDemoWorkspaceId } from '../constants/demo';
import { normalizeWorkspace, type PipelineStage, type Workspace } from '../utils/workspaces';

export type { PipelineStage, Workspace };

export interface PipelineValueByCurrency {
  currency: string;
  /** Exact decimal text. Never summed across currencies. */
  value: string;
  project_count: number;
}

/** Editable details of one project, as the account overview sends them (all optional: older servers omit them). */
export interface OverviewProject {
  project_id: string;
  description?: string | null;
  client_id?: string | null;
  client_name?: string | null;
  event_date?: string | null;
  date_start?: string | null;
  date_end?: string | null;
  location_city?: string | null;
  location_country?: string | null;
}

export interface AccountOverview {
  /** Per-project details used to prefill Edit project; absent on older servers. */
  projects?: OverviewProject[];
  activeProjects: number;
  /** One number only when every project shares a currency; null otherwise. */
  pipelineValue: number | null;
  pipelineValueByCurrency: PipelineValueByCurrency[];
  /** Projects with a contract value but no currency set. */
  projectsMissingCurrency: number;
  /** True when some money was left out because this person may not see it. */
  moneyHidden: boolean;
  tasksDue: number;
  upcomingEvents: number;
  stageBreakdown: Record<PipelineStage, number>;
}

export interface SubscriptionInfo {
  status: 'trialing' | 'active' | 'past_due' | 'canceled';
  trial_ends_at: string | null;
  plan: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True after the API rejected an expired login; the sign-in page explains why. */
  sessionEnded: boolean;
  /** Every project this person can open. */
  workspaces: Workspace[];
  subscription: SubscriptionInfo | null;
  login: (redirectPath?: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  /** One-click sign-in to the demo workspace. Throws an ApiError whose message is safe to show. */
  loginDemo: () => Promise<void>;
  /** True when the active project belongs to the demo workspace. */
  isDemo: boolean;
  logout: () => Promise<void>;
  verifyAuth: () => Promise<void>;
  switchWorkspace: (projectId: string) => Promise<void>;
  /** A currency is required: there is no default. */
  createWorkspace: (name: string, currency: string, dateStart?: string, dateEnd?: string) => Promise<void>;
  createLead: (name: string, currency: string, dateStart?: string) => Promise<void>;
  updatePipelineStage: (projectId: string, stage: PipelineStage) => Promise<void>;
  /** Exact decimal text (or null to clear). */
  updateContractValue: (projectId: string, value: string | null) => Promise<void>;
  fetchAccountOverview: () => Promise<AccountOverview | null>;
  /** Editable details of every project the person can see (from /planning/me), for prefilling Edit project. */
  fetchProjectDetails: () => Promise<OverviewProject[]>;
  /** Re-reads the project list in place (no switch, no reload). */
  refreshWorkspaces: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const DEV_USER: User = {
  id: 'dev-oceania',
  email: 'oceaniaalfa@gmail.com',
  name: 'Oceania Alfa',
  verified_email: true,
  role: 'owner',
};

const DEMO_UNAVAILABLE = 'The demo is not available right now.';

const CURRENCY_CODE = /^[A-Za-z]{3}$/;
const CURRENCY_REQUIRED = 'Choose a currency for this project.';

/** Status of a failed axios call, without ever touching its config (which holds the Authorization header). */
function statusOf(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : null;
}

/**
 * Turns a failed axios call to the planning API into the app's plain-language
 * ApiError, and ends the session when the login has expired.
 */
function toApiError(error: unknown, sentToken: string | null): ApiError {
  if (error instanceof ApiError) return error;
  const status = statusOf(error);
  if (status === null) return ApiError.offline();
  endSessionIfExpired(status, sentToken);
  return buildApiError(status, (error as { response?: { data?: unknown } }).response?.data);
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);

  const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

  // Backend URL - adjust if needed
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const GG_API_URL = process.env.REACT_APP_GG_API_URL || 'http://localhost:3000';
  const GG_API_KEY = import.meta.env.VITE_ANS_WEB_API_KEY || '';

  /** Headers for the planning API: the shared key plus the signed-in bearer token, when there is one. */
  const ggHeaders = useCallback((token?: string | null): Record<string, string> => {
    const headers: Record<string, string> = { 'x-api-key': GG_API_KEY };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, [GG_API_KEY]);

  /** The signed-in token, or an error that tells the person to sign in. */
  const requireToken = (): string => {
    const token = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
    if (!token) throw new ApiError('Your session has ended. Please sign in again.', { status: 401 });
    return token;
  };

  // When the API says the login has expired, drop the person back to the
  // sign-in page with an explanation instead of leaving a page of failed
  // screens behind.
  useEffect(() => {
    const onEnded = () => {
      setUser(null);
      setWorkspaces([]);
      setSubscription(null);
      setSessionEnded(true);
      setIsLoading(false);
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, []);

  // /planning/me returns every project this e-mail belongs to, not a single
  // guessed one. Auto-selecting when there's exactly one keeps a
  // single-project user's experience unchanged; 2+ hands the list to the
  // project switcher instead of guessing which one they meant. The signed-in
  // token is sent too: the API checks the e-mail asked about is the caller's.
  const resolveUserFromGuestGuru = useCallback(async (email: string): Promise<{
    workspaces: Workspace[];
    subscription: SubscriptionInfo | null;
  }> => {
    const token = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
    try {
      const response = await axios.get<{ email: string; workspaces?: Record<string, unknown>[]; projects?: Record<string, unknown>[]; subscription: SubscriptionInfo | null }>(
        `${GG_API_URL}/planning/me`,
        {
          params: { email },
          headers: ggHeaders(token),
        }
      );
      const rows = response.data.workspaces ?? response.data.projects ?? [];
      return {
        workspaces: rows.map(normalizeWorkspace),
        subscription: response.data.subscription ?? null,
      };
    } catch (error) {
      const status = statusOf(error);
      if (status !== null) endSessionIfExpired(status, token);
      return { workspaces: [], subscription: null };
    }
  }, [GG_API_URL, ggHeaders]);

  const applyWorkspaceContext = useCallback((email: string, resolved: { workspaces: Workspace[]; subscription: SubscriptionInfo | null }) => {
    setWorkspaces(resolved.workspaces);
    setSubscription(resolved.subscription);

    const currentProjectId = getActiveProjectId();
    const currentIsValid = resolved.workspaces.some(w => w.project_id === currentProjectId);

    // Auto-select only when there's exactly one project and nothing valid
    // is already active — the existing API token from login already
    // scopes to a real project for anyone with 2+, so we don't want to
    // silently override an explicit prior selection.
    if (!currentIsValid && resolved.workspaces.length === 1) {
      setActiveProjectId(resolved.workspaces[0].project_id);
    }

    const activeProjectId = getActiveProjectId();
    const activeWorkspace = resolved.workspaces.find(w => w.project_id === activeProjectId);
    return {
      role: activeWorkspace?.role as UserRole | undefined,
      financially_blind: activeWorkspace?.financially_blind === true,
    };
  }, []);

  const verifyAuth = useCallback(async () => {
    if (DEV_BYPASS) {
      localStorage.setItem(STORAGE_KEYS.PROFILE_OWNER_EMAIL, DEV_USER.email);
      const resolved = await resolveUserFromGuestGuru(DEV_USER.email);
      const ctx = applyWorkspaceContext(DEV_USER.email, resolved);
      setUser({ ...DEV_USER, role: ctx.role ?? DEV_USER.role, financially_blind: ctx.financially_blind });
      setIsLoading(false);
      return;
    }

    // A demo session holds only the planning-API token, so it is verified there; every other login
    // is verified by the app's own backend with its own token.
    const demo = localStorage.getItem(STORAGE_KEYS.DEMO_SESSION) === '1';
    const sessionKey = demo ? STORAGE_KEYS.GG_AUTH_TOKEN : STORAGE_KEYS.AUTH_TOKEN;
    const token = localStorage.getItem(sessionKey);
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = demo
        ? await axios.get(`${GG_API_URL}/auth/verify`, { headers: ggHeaders(token) })
        : await axios.get(`${API_BASE_URL}/auth/verify`, { headers: { 'Authorization': `Bearer ${token}` } });

      if (response.data.authenticated) {
        const raw = response.data.user;
        const verifiedUser: User = demo
          ? { ...raw, id: String(raw.user_id ?? raw.id ?? ''), verified_email: false }
          : raw;
        localStorage.setItem(STORAGE_KEYS.PROFILE_OWNER_EMAIL, verifiedUser.email);
        const resolved = await resolveUserFromGuestGuru(verifiedUser.email);
        // The planning API may have rejected the login while we asked for the
        // project list; the session-ended event has then already signed us out.
        if (!localStorage.getItem(sessionKey)) return;
        const ctx = applyWorkspaceContext(verifiedUser.email, resolved);
        setUser({ ...verifiedUser, role: ctx.role ?? undefined, financially_blind: ctx.financially_blind });
        setSessionEnded(false);
        if (!localStorage.getItem(STORAGE_KEYS.LAST_LOGIN)) {
          localStorage.setItem(STORAGE_KEYS.LAST_LOGIN, new Date().toISOString());
        }
      }
    } catch (error) {
      // Log the status only: the error object carries the request headers,
      // which include the bearer token.
      const status = statusOf(error);
      console.error('Auth verification failed:', status ?? 'no response from the server');
      setUser(null);
      if (status === 401) {
        // The login has expired. Clear every credential so a reload does not
        // retry a dead token, and tell the sign-in page why.
        clearStoredSession();
        setSessionEnded(true);
      }
      // Any other failure (network down, server error) keeps the stored
      // token: it may still be good once the connection is back.
    } finally {
      setIsLoading(false);
    }
  }, [API_BASE_URL, GG_API_URL, ggHeaders, resolveUserFromGuestGuru, applyWorkspaceContext]);

  useEffect(() => {
    if (DEV_BYPASS) {
      verifyAuth();
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const guestguruToken = urlParams.get('guestguru_token');

    if (token) {
      localStorage.removeItem(STORAGE_KEYS.DEMO_SESSION);
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      if (guestguruToken) localStorage.setItem(STORAGE_KEYS.GG_AUTH_TOKEN, guestguruToken);
      localStorage.setItem(STORAGE_KEYS.LAST_LOGIN, new Date().toISOString());
      window.history.replaceState({}, document.title, window.location.pathname);
      verifyAuth();
    } else {
      const storedToken = localStorage.getItem(STORAGE_KEYS.DEMO_SESSION) === '1'
        ? localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN)
        : localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (storedToken) {
        verifyAuth();
      } else {
        setIsLoading(false);
      }
    }
  }, [verifyAuth]);

  const loginWithPassword = async (email: string, password: string) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login/password`, { email, password });
    const token: string = response.data.access_token;
    localStorage.removeItem(STORAGE_KEYS.DEMO_SESSION);
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    if (response.data.guestguru_token) {
      localStorage.setItem(STORAGE_KEYS.GG_AUTH_TOKEN, response.data.guestguru_token);
    }
    localStorage.setItem(STORAGE_KEYS.LAST_LOGIN, new Date().toISOString());
    setSessionEnded(false);
    await verifyAuth();
  };

  // The one-click demo sign-in: no password, no Google. The API answers 404 when the demo is switched off,
  // which is shown as a plain message and never retried.
  const loginDemo = async () => {
    let response;
    try {
      response = await axios.post(`${GG_API_URL}/auth/login/demo`, null, { headers: ggHeaders() });
    } catch (error) {
      const status = statusOf(error);
      if (status === null) throw ApiError.offline();
      if (status === 404 || status === 503) throw new ApiError(DEMO_UNAVAILABLE, { status });
      if (status === 429) throw new ApiError('Too many people are trying the demo. Please wait a minute and try again.', { status });
      throw new ApiError(DEMO_UNAVAILABLE, { status });
    }
    const token = response.data?.token;
    const projectId = response.data?.project_id ?? response.data?.wedding_id;
    if (typeof token !== 'string' || !token) throw new ApiError(DEMO_UNAVAILABLE, { status: 502 });
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.setItem(STORAGE_KEYS.GG_AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.DEMO_SESSION, '1');
    localStorage.setItem(STORAGE_KEYS.LAST_LOGIN, new Date().toISOString());
    if (typeof projectId === 'string' && projectId) setActiveProjectId(projectId);
    setSessionEnded(false);
    await verifyAuth();
  };

  const login = async (redirectPath?: string) => {
    if (DEV_BYPASS) {
      setUser(DEV_USER);
      return;
    }

    try {
      // Get OAuth URL from backend
      const response = await axios.get(`${API_BASE_URL}/auth/login`, {
        params: redirectPath ? { state: redirectPath } : undefined,
      });
      const authUrl = response.data.auth_url;

      if (!authUrl || typeof authUrl !== 'string') {
        throw new Error('Backend did not return a valid auth URL. Check that the API server is running.');
      }

      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (error) {
      console.error('Login failed:', statusOf(error) ?? (error instanceof Error ? error.message : 'unknown error'));
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call backend logout (though it's mostly client-side)
      await axios.post(`${API_BASE_URL}/auth/logout`);
    } catch (error) {
      console.error('Logout failed:', statusOf(error) ?? 'no response from the server');
      // Still clear local state even if API call fails
    }
    clearStoredSession();
    setSessionEnded(false);
    setUser(null);
    setWorkspaces([]);
    setSubscription(null);
  };

  // Mints a fresh API token scoped to a different project the caller
  // already belongs to, then reloads — the simplest way to guarantee every
  // already-fetched piece of app state resets cleanly, matching how login
  // itself works today.
  const switchWorkspace = async (projectId: string) => {
    const token = requireToken();
    let response;
    try {
      response = await axios.post(
        `${GG_API_URL}/auth/select-workspace`,
        { project_id: projectId },
        { headers: ggHeaders(token) }
      );
    } catch (error) {
      throw toApiError(error, token);
    }

    localStorage.setItem(STORAGE_KEYS.GG_AUTH_TOKEN, response.data.token);
    setActiveProjectId(projectId);
    reloadApp();
  };

  // Creates a brand new project owned by the current account, then
  // immediately switches into it. The project's currency is chosen here and
  // is never defaulted.
  const createWorkspace = async (name: string, currency: string, dateStart?: string, dateEnd?: string) => {
    if (!CURRENCY_CODE.test(currency.trim())) throw new ApiError(CURRENCY_REQUIRED, { status: 400, fieldErrors: { currency: CURRENCY_REQUIRED } });
    const token = requireToken();
    let response;
    try {
      response = await axios.post(
        `${GG_API_URL}/planning/workspaces`,
        { name, currency: currency.trim().toUpperCase(), date_start: dateStart, date_end: dateEnd },
        { headers: ggHeaders(token) }
      );
    } catch (error) {
      throw toApiError(error, token);
    }

    await switchWorkspace(response.data.project_id ?? response.data.wedding_id);
  };

  // Refreshes the project list in place (no active-project switch, no
  // reload) — used by the Sales Pipeline page, where adding a lead or
  // moving a card's stage shouldn't yank the planner out of what they're
  // currently looking at.
  const refreshWorkspaces = useCallback(async () => {
    if (!user?.email) return;
    const resolved = await resolveUserFromGuestGuru(user.email);
    setWorkspaces(resolved.workspaces);
    setSubscription(resolved.subscription);
  }, [user, resolveUserFromGuestGuru]);

  // Adds a pipeline card at the 'lead' stage — a lead IS a real project from
  // day one (same as createWorkspace), just earlier in its lifecycle, so no
  // separate "convert" step is needed later. Unlike createWorkspace, this
  // does NOT switch into it or reload.
  const createLead = async (name: string, currency: string, dateStart?: string) => {
    if (!CURRENCY_CODE.test(currency.trim())) throw new ApiError(CURRENCY_REQUIRED, { status: 400, fieldErrors: { currency: CURRENCY_REQUIRED } });
    const token = requireToken();
    try {
      await axios.post(
        `${GG_API_URL}/planning/workspaces`,
        { name, currency: currency.trim().toUpperCase(), date_start: dateStart, pipeline_stage: 'lead' },
        { headers: ggHeaders(token) }
      );
    } catch (error) {
      throw toApiError(error, token);
    }
    await refreshWorkspaces();
  };

  const updatePipelineStage = async (projectId: string, stage: PipelineStage) => {
    const token = requireToken();
    try {
      await axios.put(
        `${GG_API_URL}/planning/workspaces/${projectId}/pipeline-stage`,
        { pipeline_stage: stage },
        { headers: ggHeaders(token) }
      );
    } catch (error) {
      throw toApiError(error, token);
    }
    await refreshWorkspaces();
  };

  const updateContractValue = async (projectId: string, contractValue: string | null) => {
    const token = requireToken();
    try {
      await axios.put(
        `${GG_API_URL}/planning/workspaces/${projectId}/contract-value`,
        { contract_value: contractValue },
        { headers: ggHeaders(token) }
      );
    } catch (error) {
      throw toApiError(error, token);
    }
    await refreshWorkspaces();
  };

  // Powers the "At a glance" account overview page — a lightweight,
  // on-demand fetch (not part of the /planning/me bootstrap) since it's
  // only needed when that one page is actually open. A failure is thrown, not
  // swallowed, so the page can say it could not load instead of showing zeros.
  const fetchAccountOverview = useCallback(async (): Promise<AccountOverview | null> => {
    if (!user?.email) return null;
    const token = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
    try {
      const response = await axios.get<AccountOverview>(`${GG_API_URL}/planning/account-overview`, {
        params: { email: user.email },
        headers: ggHeaders(token),
      });
      return response.data;
    } catch (error) {
      throw toApiError(error, token);
    }
  }, [user, GG_API_URL, ggHeaders]);

  // Editable project details (description, client, dates, place) ride along with the
  // /planning/me project list, not the overview. Fetched on demand when the Edit
  // project drawer opens, so a stale login never prefills old values. Older servers
  // omit the fields; every one is optional.
  const fetchProjectDetails = useCallback(async (): Promise<OverviewProject[]> => {
    if (!user?.email) return [];
    const token = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
    const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
    try {
      const response = await axios.get<{ workspaces?: Record<string, unknown>[]; projects?: Record<string, unknown>[] }>(
        `${GG_API_URL}/planning/me`,
        { params: { email: user.email }, headers: ggHeaders(token) },
      );
      const rows = response.data.workspaces ?? response.data.projects ?? [];
      return rows.map(r => ({
        project_id: String(r.project_id ?? r.wedding_id ?? ''),
        description: str(r.description),
        client_id: str(r.client_id),
        client_name: str(r.client_name),
        event_date: str(r.event_date),
        date_start: str(r.date_start),
        date_end: str(r.date_end),
        location_city: str(r.location_city),
        location_country: str(r.location_country),
      }));
    } catch (error) {
      throw toApiError(error, token);
    }
  }, [user, GG_API_URL, ggHeaders]);

  const activeWorkspace = workspaces.find(w => w.project_id === getActiveProjectId());
  const isDemo = isDemoWorkspaceId(activeWorkspace?.workspace_id ?? user?.workspace_id);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    sessionEnded,
    workspaces,
    subscription,
    login,
    loginWithPassword,
    loginDemo,
    isDemo,
    logout,
    verifyAuth,
    switchWorkspace,
    createWorkspace,
    createLead,
    updatePipelineStage,
    updateContractValue,
    fetchAccountOverview,
    fetchProjectDetails,
    refreshWorkspaces,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
